import { enableAllPlugins, produce } from 'immer';
import _ from 'lodash';
import moment from 'moment';
import momentTimezone from 'moment-timezone';

import {
  checkboxFilterKeys,
  dateFilterKeys,
  multiSelectIdsFilterKeys,
} from 'components/projectsDashboard/hooks/useProjectFilters';

import { clampNumber } from 'components/helpers/numbers';

enableAllPlugins();

function filterKey({ filter }) {
  return _.snakeCase(filter.replace('Filter', ''));
}

export const defaultFilterParams = { archived: { eq: false } };
export const defaultFolder = { name: '' };

export function buildFilterParams({
  contributorId,
  filters,
  folderId,
  visibleFilters,
}) {
  const selectedVisibleFilters = filters.selectedFilters.filter((filterKey) =>
    visibleFilters.includes(filterKey),
  );

  return produce({ ...defaultFilterParams }, (draftState) => {
    selectedVisibleFilters.forEach((selectedFilter) => {
      if (selectedFilter === 'folderFilter' && folderId) return;

      if (multiSelectIdsFilterKeys.includes(selectedFilter)) {
        assignMultiSelectIdsFilterParams({
          draftState,
          selectedFilter,
          suffix: 'id',
        });
      } else if (dateFilterKeys.includes(selectedFilter)) {
        assignDateFilterParams({ draftState, selectedFilter });
      } else if (checkboxFilterKeys.includes(selectedFilter)) {
        assignCheckboxFilterParams({ draftState, selectedFilter });
      } else if (selectedFilter.startsWith('dashboardConfigurableField')) {
        assignMultiSelectIdsFilterParams({ draftState, selectedFilter });
      }
    });

    if (contributorId) draftState.contributor_id = { eq: contributorId };
    if (folderId) draftState.folder_id = { eq: folderId };
  });

  function assignMultiSelectIdsFilterParams({
    draftState,
    selectedFilter,
    suffix,
  }) {
    const { selectedIds } = filters[selectedFilter];

    if (
      selectedIds.length === 0 ||
      Object.keys(filters.validationErrors[selectedFilter]).length > 0
    )
      return;

    draftState[
      `${filterKey({ filter: selectedFilter })}${suffix ? `_${suffix}` : ''}`
    ] = selectedIds.length > 1 ? { in: selectedIds } : { eq: selectedIds[0] };
  }

  function assignDateFilterParams({ draftState, selectedFilter }) {
    const {
      after,
      before,
      customDateRangeFrom,
      customDateRangeTo,
      onExactDate,
      period,
      withinTheLastFrequency,
      withinTheLastInterval,
      withinTheNextFrequency,
      withinTheNextInterval,
    } = filters[selectedFilter];
    if (Object.keys(filters.validationErrors[selectedFilter]).length > 0)
      return;

    const dateFormat = 'YYYY-MM-DD';
    const london = momentTimezone.tz('Europe/London');

    let eq;
    let gt;
    let gte;
    let lt;
    let lte;

    switch (true) {
      case period === 'withinTheNext':
        gte = london.clone().format(dateFormat);
        lte = london
          .clone()
          .add(withinTheNextFrequency, withinTheNextInterval)
          .format(dateFormat);
        break;
      case period === 'withinTheLast':
        gte = london
          .clone()
          .subtract(withinTheLastFrequency, withinTheLastInterval)
          .format(dateFormat);
        lte = london.clone().format(dateFormat);
        break;
      case ['thisWeek', 'thisMonth', 'thisQuarter', 'thisYear'].includes(
        period,
      ):
        {
          const thisUnitOfTime = period.replace('this', '').toLowerCase();

          gte = london
            .clone()
            .startOf(thisUnitOfTime === 'week' ? 'isoWeek' : thisUnitOfTime)
            .format(dateFormat);
          lte = london
            .clone()
            .endOf(thisUnitOfTime === 'week' ? 'isoWeek' : thisUnitOfTime)
            .format(dateFormat);
        }
        break;
      case ['nextWeek', 'nextMonth', 'nextQuarter', 'nextYear'].includes(
        period,
      ):
        {
          const nextUnitOfTime = period.replace('next', '').toLowerCase();

          gte = london
            .clone()
            .add(1, nextUnitOfTime)
            .startOf(nextUnitOfTime === 'week' ? 'isoWeek' : nextUnitOfTime)
            .format(dateFormat);
          lte = london
            .clone()
            .add(1, nextUnitOfTime)
            .endOf(nextUnitOfTime === 'week' ? 'isoWeek' : nextUnitOfTime)
            .format(dateFormat);
        }
        break;
      case ['lastWeek', 'lastMonth', 'lastQuarter', 'lastYear'].includes(
        period,
      ):
        {
          const lastUnitOfTime = period.replace('last', '').toLowerCase();

          gte = london
            .clone()
            .subtract(1, lastUnitOfTime)
            .startOf(lastUnitOfTime === 'week' ? 'isoWeek' : lastUnitOfTime)
            .format(dateFormat);
          lte = london
            .clone()
            .subtract(1, lastUnitOfTime)
            .endOf(lastUnitOfTime === 'week' ? 'isoWeek' : lastUnitOfTime)
            .format(dateFormat);
        }
        break;
      case period === 'today':
        eq = london.clone().format(dateFormat);
        break;
      case period === 'tomorrow':
        eq = london.clone().add(1, 'day').format(dateFormat);
        break;
      case period === 'yesterday':
        eq = london.clone().subtract(1, 'day').format(dateFormat);
        break;
      case period === 'before':
        lt = moment.parseZone(before).format(dateFormat);
        break;
      case period === 'after':
        gt = moment.parseZone(after).format(dateFormat);
        break;
      case period === 'onExactDate':
        eq = moment.parseZone(onExactDate).format(dateFormat);
        break;
      case period === 'customDateRange':
        gte = moment.parseZone(customDateRangeFrom).format(dateFormat);
        lte = moment.parseZone(customDateRangeTo).format(dateFormat);
        break;
      case period === null:
        eq = null;
        break;
    }

    draftState[filterKey({ filter: selectedFilter })] = {
      eq,
      gt,
      gte,
      lt,
      lte,
    };
  }

  function assignCheckboxFilterParams({ draftState, selectedFilter }) {
    const checkedCheckboxes = Object.keys(filters[selectedFilter])
      .filter((key) => filters[selectedFilter][key])
      .map((key) => _.snakeCase(key));

    if (
      checkedCheckboxes.length === 0 ||
      Object.keys(filters.validationErrors[selectedFilter]).length > 0
    )
      return;

    draftState[filterKey({ filter: selectedFilter })] =
      checkedCheckboxes.length > 1 ?
        { in: checkedCheckboxes }
      : { eq: checkedCheckboxes[0] };
  }
}

export function buildSelectionsParams({ columnVisibility, folderId }) {
  const overriddenColumnVisibility =
    folderId ? { ...columnVisibility, folder: false } : columnVisibility;

  return Object.keys(overriddenColumnVisibility)
    .filter(
      (key) =>
        !['selector', 'actions'].includes(key) &&
        overriddenColumnVisibility[key],
    )
    .map((key) => _.snakeCase(key));
}

export function buildSortParams({ columnSort }) {
  return {
    column: _.snakeCase(columnSort.columnId),
    direction: columnSort.direction,
  };
}

export function dashboardConfigurableFieldKey({
  dashboardConfigurableField,
  suffix,
}) {
  return `dashboardConfigurableField${dashboardConfigurableField.id}${suffix ? `${suffix}` : ''}`;
}

export function buildBulkUpdateFilterSelectionConfigurationsParams({
  filters,
  visibleFilters,
}) {
  const selectedVisibleFilters = filters.selectedFilters.filter((filterKey) =>
    visibleFilters.includes(filterKey),
  );

  const unselectedVisibleFilters = visibleFilters.filter(
    (filterKey) => !filters.selectedFilters.includes(filterKey),
  );

  return produce({}, (draftState) => {
    selectedVisibleFilters.forEach((selectedFilter) => {
      if (multiSelectIdsFilterKeys.includes(selectedFilter)) {
        assignMultiSelectIdsFilterSelectionsParams({
          draftState,
          selectedFilter,
        });
      } else if (dateFilterKeys.includes(selectedFilter)) {
        assignDateFilterSelectionParams({
          draftState,
          selectedFilter,
        });
      } else if (checkboxFilterKeys.includes(selectedFilter)) {
        assignCheckboxFilterSelectionsParams({
          draftState,
          selectedFilter,
        });
      } else if (selectedFilter.startsWith('dashboardConfigurableField')) {
        assignMultiSelectIdsFilterSelectionsParams({
          draftState,
          selectedFilter,
        });
      }
    });

    unselectedVisibleFilters.forEach((unselectedFilter) => {
      draftState[filterKey({ filter: unselectedFilter })] = [];
    });
  });

  function assignMultiSelectIdsFilterSelectionsParams({
    draftState,
    selectedFilter,
  }) {
    if (Object.keys(filters.validationErrors[selectedFilter]).length > 0) {
      draftState[filterKey({ filter: selectedFilter })] = [];
    } else {
      draftState[filterKey({ filter: selectedFilter })] =
        filters[selectedFilter].selectedIds;
    }
  }

  function assignDateFilterSelectionParams({ draftState, selectedFilter }) {
    if (Object.keys(filters.validationErrors[selectedFilter]).length > 0) {
      draftState[filterKey({ filter: selectedFilter })] = [];
    } else {
      const { period } = filters[selectedFilter];

      const dateFilterSelectionParams = {
        period: _.snakeCase(period) || null,
        date_one: null,
        date_two: null,
        duration: null,
      };

      switch (true) {
        case ['withinTheNext', 'withinTheLast'].includes(period): {
          const frequency = filters[selectedFilter][`${period}Frequency`];
          const interval = { days: 'D', weeks: 'W', months: 'M', years: 'Y' }[
            filters[selectedFilter][`${period}Interval`]
          ];
          dateFilterSelectionParams.duration = `P${frequency}${interval}`;
          break;
        }
        case ['before', 'after', 'onExactDate'].includes(period):
          dateFilterSelectionParams.date_one = moment
            .parseZone(filters[selectedFilter][period])
            .format('YYYY-MM-DD');
          break;
        case period === 'customDateRange':
          dateFilterSelectionParams.date_one = moment
            .parseZone(filters[selectedFilter].customDateRangeFrom)
            .format('YYYY-MM-DD');
          dateFilterSelectionParams.date_two = moment
            .parseZone(filters[selectedFilter].customDateRangeTo)
            .format('YYYY-MM-DD');
          break;
      }

      draftState[filterKey({ filter: selectedFilter })] = [
        dateFilterSelectionParams,
      ];
    }
  }

  function assignCheckboxFilterSelectionsParams({
    draftState,
    selectedFilter,
  }) {
    if (Object.keys(filters.validationErrors[selectedFilter]).length > 0) {
      draftState[filterKey({ filter: selectedFilter })] = [];
    } else {
      draftState[filterKey({ filter: selectedFilter })] = Object.keys(
        filters[selectedFilter],
      )
        .filter((key) => filters[selectedFilter][key])
        .map((key) => _.snakeCase(key));
    }
  }
}

export function buildBulkUpdateTableConfigurationsParams({
  tableConfiguration,
}) {
  const {
    columnOrder,
    columnSizing,
    columnSizingDefaults,
    columnSort,
    columnVisibility,
  } = tableConfiguration;

  return produce({}, (draftState) => {
    draftState.columns = columnOrder.reduce((acc, columnId) => {
      if (
        !['selector', 'live', 'actions'].includes(columnId) &&
        columnVisibility[columnId]
      ) {
        let width;

        if (
          columnSizing &&
          Object.prototype.hasOwnProperty.call(columnSizing, columnId)
        ) {
          width = clampNumber(
            Math.round(columnSizing[columnId]),
            columnSizingDefaults[columnId].minSize,
            columnSizingDefaults[columnId].maxSize,
          );
        }

        acc.push({ column: _.snakeCase(columnId), width });
      }

      return acc;
    }, []);

    if (columnSort.isUnsorted) {
      draftState.sort = null;
    } else {
      draftState.sort = {
        column: _.snakeCase(columnSort.columnId),
        direction: columnSort.direction,
      };
    }
  });
}
